import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Slide,
  IconButton,
  useTheme,
  Collapse,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { Close, Cookie, Settings as SettingsIcon, CheckCircle } from '@mui/icons-material';
import { useLanguage } from '../hooks/useLanguage';
import { 
  getCookieConsent, 
  saveCookieConsent, 
  defaultCookiePreferences,
  CookiePreferences 
} from '../utils/cookieConsent';
import { getLocalizedLegalPath } from '../utils/legalLinks';

const CookieConsentBanner: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const theme = useTheme();
  const privacyPath = getLocalizedLegalPath('privacy', currentLanguage);
  
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultCookiePreferences);

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = getCookieConsent();
    if (!hasConsented) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSaveConsent = (prefs: CookiePreferences) => {
    saveCookieConsent(prefs);
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    handleSaveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(necessaryOnly);
    handleSaveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    handleSaveConsent(preferences);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  if (!isVisible) return null;

  return (
    <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          borderRadius: { xs: '16px 16px 0 0', md: 0 },
          maxHeight: '90vh',
          overflow: 'auto',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderTop: `3px solid ${theme.palette.primary.main}`,
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Cookie sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#fff',
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  mb: 0.5,
                }}
              >
                {t('cookies.title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '0.8rem', md: '0.9rem' },
                  lineHeight: 1.5,
                }}
              >
                {t('cookies.description')}
              </Typography>
            </Box>
            <IconButton
              onClick={handleAcceptNecessary}
              sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: '#fff' } }}
              aria-label={t('buttons.close')}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Cookie Details */}
          <Collapse in={showDetails}>
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
              
              {/* Necessary Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={true}
                      disabled
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.success.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.success.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                        {t('cookies.necessary.title')}
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: '0.7rem',
                            color: theme.palette.success.main,
                            backgroundColor: 'rgba(138, 201, 38, 0.2)',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {t('cookies.required')}
                        </Typography>
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                        {t('cookies.necessary.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Box>

              {/* Analytics Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.analytics}
                      onChange={handlePreferenceChange('analytics')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                        {t('cookies.analytics.title')}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                        {t('cookies.analytics.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Box>

              {/* Marketing Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.marketing}
                      onChange={handlePreferenceChange('marketing')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                        {t('cookies.marketing.title')}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                        {t('cookies.marketing.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
            </Box>
          </Collapse>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="text"
              startIcon={<SettingsIcon />}
              onClick={() => setShowDetails(!showDetails)}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textTransform: 'none',
                fontSize: '0.85rem',
                '&:hover': {
                  color: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                order: { xs: 3, md: 1 },
              }}
            >
              {showDetails ? t('cookies.hideDetails') : t('cookies.showDetails')}
            </Button>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                order: { xs: 1, md: 2 },
              }}
            >
              {showDetails ? (
                <Button
                  variant="outlined"
                  onClick={handleSavePreferences}
                  startIcon={<CheckCircle />}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      borderColor: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('cookies.savePreferences')}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleAcceptNecessary}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      borderColor: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {t('cookies.acceptNecessary')}
                </Button>
              )}

              <Button
                variant="contained"
                onClick={handleAcceptAll}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  boxShadow: `0 4px 14px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                  },
                }}
              >
                {t('cookies.acceptAll')}
              </Button>
            </Box>
          </Box>

          {/* Privacy Policy Link */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}
            >
              {t('cookies.privacyNote')}{' '}
              <a
                href={privacyPath}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: theme.palette.primary.light,
                  textDecoration: 'underline',
                }}
              >
                {t('footerLinks.privacy')}
              </a>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default CookieConsentBanner;
